import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Output,
  afterNextRender,
  effect,
  inject,
} from '@angular/core';
import {
  NgpMenuTrigger,
  injectMenuTriggerState,
  provideMenuConfig,
} from 'ng-primitives/menu';

import { resolveSuiBoundary } from '@sensika/sui/shared';

/**
 * `[suiMenuTrigger]` — turns an element into an action-menu trigger, pointing at
 * an `<ng-template>` that holds a `<sui-menu>`. Thin wrapper over ng-primitives'
 * `NgpMenuTrigger`: passes the engine inputs straight through (no transforms —
 * §12 ② of the CRAFTING-GUIDE) and *adds* an imperative surface the call sites
 * use (`isOpen()`, `open()`/`close()`/`toggle()`, `(openChange)`) by reading the
 * primitive's `open` signal.
 *
 * `exportAs: 'suiMenu'` so a template ref (`#m="suiMenu"`) can read `.isOpen()`
 * (e.g. to drive the trigger's `[expanded]` state) and control it imperatively.
 *
 * Placement uses floating-ui's native 12-point vocab (`bottom-start` default,
 * `top`/`bottom`/`left`/`right` + `-start`/`-end`).
 *
 * `scrollBehavior` defaults to `'reposition'` here (ng-primitives' own default is
 * `'block'`, which would lock page scroll while open). Set once via
 * `provideMenuConfig` on this element's injector, which `NgpMenuTrigger` (a host
 * directive on the same element) reads as its input default.
 *
 * Container defaults to the nearest `data-sui-popover-boundary` pane (else
 * `body`), resolved from the trigger's own DOM position, so in-dialog menus
 * stack correctly with no wiring. `[suiMenuContainer]` overrides: a template
 * binding makes the engine input controlled and our programmatic `set()` a
 * silent no-op (ng-primitives controlled-input semantics).
 */
@Directive({
  selector: '[suiMenuTrigger]',
  standalone: true,
  exportAs: 'suiMenu',
  hostDirectives: [
    {
      directive: NgpMenuTrigger,
      inputs: [
        'ngpMenuTrigger: suiMenuTrigger',
        'ngpMenuTriggerPlacement: suiMenuPlacement',
        'ngpMenuTriggerDisabled: suiMenuTriggerDisabled',
        'ngpMenuTriggerContainer: suiMenuContainer',
        'ngpMenuTriggerOffset: suiMenuOffset',
      ],
    },
  ],
  providers: [provideMenuConfig({ scrollBehavior: 'reposition' })],
})
export class SuiMenuTriggerDirective {
  private readonly trigger = inject(NgpMenuTrigger);
  private readonly state = injectMenuTriggerState();
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * Emits `true` on open, `false` on close. `NgpMenuTrigger` has no such output
   * (it exposes `open` as a signal), so we bridge from an effect. Used by the
   * sites that close a coordinated `[suiTooltip]` on open.
   */
  @Output() readonly openChange = new EventEmitter<boolean>();

  constructor() {
    let previous = this.trigger.open();
    effect(() => {
      const open = this.trigger.open();
      if (open !== previous) {
        previous = open;
        this.openChange.emit(open);
      }
    });

    afterNextRender(() => this.applyAutoContainer());
  }

  /**
   * Re-resolved on every open intent (not just once) because the trigger may be
   * portaled after `afterNextRender` (e.g. inside a popover panel). Repeated
   * `set()` is harmless: a no-op when `[suiMenuContainer]` is bound, and the
   * engine snapshots the container at first open anyway.
   */
  @HostListener('pointerdown')
  @HostListener('keydown')
  protected applyAutoContainer(): void {
    this.state().container.set(resolveSuiBoundary(this.host.nativeElement));
  }

  /** Current open state (the primitive exposes `open` as a signal, not a method). */
  isOpen(): boolean {
    return this.trigger.open();
  }

  open(): void {
    if (!this.trigger.open()) {
      this.applyAutoContainer();
      this.trigger.show();
    }
  }

  close(): void {
    if (this.trigger.open()) {
      this.trigger.hide();
    }
  }

  toggle(): void {
    if (this.trigger.open()) {
      this.trigger.hide();
    } else {
      this.applyAutoContainer();
      this.trigger.show();
    }
  }
}
